import { getRegistryScopeTopics } from '../curriculumRegistry';

const IN_FLIGHT_GENERATION_STAGES = new Set(['queued', 'extracting', 'generating', 'validating', 'saving']);

const deriveReviewMode = (file = {}) => {
  const explicitMode = String(file.review_mode || '').trim().toLowerCase();
  if (explicitMode === 'fixed' || explicitMode === 'generated' || explicitMode === 'source') return explicitMode;
  const fileType = String(file.file_type || '').trim().toLowerCase();
  const contentRole = String(file.content_role || '').trim().toLowerCase();
  const source = String(file.source || '').trim().toLowerCase();
  if (contentRole === 'lesson_source') return 'source';
  if (fileType === 'fixed' || fileType === 'fixed_questions' || ['fixed', 'restored_import', 'client_provided'].includes(source)) return 'fixed';
  if (
    fileType === 'lesson'
    || contentRole === 'question_set'
    || (file.source_learning_file_id !== undefined && file.source_learning_file_id !== null)
    || file.generation_status
    || file.generation_stage
    || (file.requested_question_count !== undefined && file.requested_question_count !== null)
  ) return 'generated';
  return 'fixed';
};

export const getGenerationStatusView = (file = {}) => {
  const reviewMode = deriveReviewMode(file);
  const isGenerated = reviewMode === 'generated';
  const status = isGenerated ? String(file.generation_status || '').trim().toLowerCase() : reviewMode === 'source' ? String(file.generation_status || '').trim().toLowerCase() : 'not_applicable';
  const stage = isGenerated ? String(file.generation_stage || status).trim().toLowerCase() : reviewMode === 'source' ? String(file.generation_stage || status).trim().toLowerCase() : 'not_applicable';
  const persistedQuestionCount = Number(file.question_count);
  const completed = Number.isFinite(persistedQuestionCount) && persistedQuestionCount >= 0
    ? persistedQuestionCount
    : Number(file.generation_completed_count);
  const requested = Number(file.requested_question_count);
  const inProgress = IN_FLIGHT_GENERATION_STAGES.has(status) || IN_FLIGHT_GENERATION_STAGES.has(stage);
  const partial = status === 'partial_failed' || stage === 'partial_failed';
  const failed = partial || status === 'failed' || stage === 'failed';
  const ready = status === 'ready_for_review' || stage === 'completed';
  const safeCompleted = Number.isFinite(completed) && completed >= 0 ? completed : null;
  const safeRequested = Number.isFinite(requested) && requested > 0 ? requested : null;
  const remaining = Number(file.generation_remaining_count);
  const safeRemaining = Number.isFinite(requested) && requested > 0 && Number.isFinite(persistedQuestionCount) && persistedQuestionCount >= 0
    ? Math.max(0, requested - persistedQuestionCount)
    : Number.isFinite(remaining) && remaining >= 0 ? remaining : null;
  return {
    status,
    stage,
    inProgress,
    failed,
    partial,
    ready,
    canApprove: ready,
    canRetry: failed,
    retryCount: safeRemaining,
    label: inProgress ? 'Generating questions' : partial ? 'Question generation partially failed' : failed ? 'Question generation failed' : ready ? 'Ready for Review' : 'Question generation',
    progressLabel: (inProgress || partial) && safeCompleted !== null && safeRequested !== null ? `${safeCompleted} / ${safeRequested} completed` : null,
  };
};

const normalizeRegistryDimension = (entry) => {
  if (typeof entry === 'string') {
    const value = entry.trim();
    return value ? { value, displayLabel: value } : null;
  }
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const value = typeof entry.value === 'string' ? entry.value.trim() : '';
  const displayLabel = typeof entry.display_label === 'string' ? entry.display_label.trim() : value;
  return value && displayLabel ? { value, displayLabel } : null;
};

const getRegistryDimensionOptions = (registry, key) => (
  Array.isArray(registry?.[key])
    ? registry[key].map(normalizeRegistryDimension).filter(Boolean)
    : []
);

export const getQuestionFolderPath = (gradeLevel, difficulty) => {
  const grade = normalizeLearningMetadataValue(gradeLevel);
  const level = normalizeDifficultyValue(difficulty);
  if (!grade) return 'Questions/';
  if (!level) return `Questions/${grade}/`;
  return `Questions/${grade}/${level}`;
};

const normalizeLearningMetadataValue = (value) => String(value || '').trim();

export const normalizeDifficultyValue = (value) => {
  const difficulty = normalizeLearningMetadataValue(value);
  if (/^(normal|average|medium|normal\s*\/\s*average)$/i.test(difficulty)) return 'Normal';
  if (/^(difficult|hard)$/i.test(difficulty)) return 'Difficult';
  if (/^easy$/i.test(difficulty)) return 'Easy';
  return difficulty;
};

export const getGradeLevels = (registry) => getRegistryDimensionOptions(registry, 'grades').map((entry) => entry.value);

export const getDifficultyLevels = (registry) => getRegistryDimensionOptions(registry, 'difficulties').map((entry) => entry.value);

export const getQuestionFolderStructure = (registry) => getRegistryDimensionOptions(registry, 'grades').map((grade) => ({
  grade: grade.value,
  folderName: grade.displayLabel,
  godotFolderName: grade.value.replace(/\s+/g, ''),
  difficulties: getDifficultyLevels(registry),
}));

export const getMathTopicsForGradeDifficulty = (gradeLevel, difficulty, registry) => {
  const grade = normalizeLearningMetadataValue(gradeLevel);
  const level = normalizeDifficultyValue(difficulty);
  return getRegistryScopeTopics(registry, grade, level).map((topic) => topic.display_label);
};

export const getMathTopicsForGrade = (gradeLevel, registry) => {
  const grade = normalizeLearningMetadataValue(gradeLevel);
  return Array.from(new Set(getDifficultyLevels(registry)
    .flatMap((difficulty) => getMathTopicsForGradeDifficulty(grade, difficulty, registry))));
};

export const isValidGradeLevel = (value, registry) => getGradeLevels(registry).includes(normalizeLearningMetadataValue(value));

export const isValidDifficulty = (value, registry) => getDifficultyLevels(registry).includes(normalizeDifficultyValue(value));

export const isValidMathTopicForGradeDifficulty = (gradeLevel, difficulty, topic, registry) => {
  const selectedTopic = normalizeLearningMetadataValue(topic);
  return getMathTopicsForGradeDifficulty(gradeLevel, difficulty, registry).includes(selectedTopic);
};

export const isValidMathTopicForGrade = (gradeLevel, topic, registry) => {
  const selectedTopic = normalizeLearningMetadataValue(topic);
  return getMathTopicsForGrade(gradeLevel, registry).includes(selectedTopic);
};

export const normalizeMathTopicForGradeDifficulty = (gradeLevel, difficulty, topic, registry) => {
  const options = getMathTopicsForGradeDifficulty(gradeLevel, difficulty, registry);
  const selectedTopic = normalizeLearningMetadataValue(topic);
  return options.includes(selectedTopic) ? selectedTopic : options[0] || '';
};

export const normalizeMathTopicForGrade = (gradeLevel, topic, registry) => {
  const options = getMathTopicsForGrade(gradeLevel, registry);
  const selectedTopic = normalizeLearningMetadataValue(topic);
  return options.includes(selectedTopic) ? selectedTopic : options[0] || '';
};

export const inferLearningFileUploadType = (fileName) => {
  const normalizedName = String(fileName || '').trim().toLowerCase();
  if (/\.(pdf|pptx)$/.test(normalizedName)) return 'lesson';
  if (normalizedName.endsWith('.json') || normalizedName.endsWith('.csv')) return 'fixed_questions';
  return '';
};

export const isSupportedLearningUpload = (fileName, fileType) => {
  const normalizedName = String(fileName || '').trim().toLowerCase();
  const normalizedType = String(fileType || '').trim().toLowerCase();
  if (normalizedType === 'lesson') return /\.(pdf|pptx|docx)$/.test(normalizedName);
  if (normalizedType === 'fixed_questions') {
    return /\.(docx|pdf|json|csv)$/.test(normalizedName);
  }
  return false;
};

export const formatLearningFileSize = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const normalizeStorageSize = (value) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : 0;
};

const countFixedQuestionJson = (content) => {
  const payload = JSON.parse(content);
  if (!Array.isArray(payload)) return 0;
  return payload.filter((item) => {
    const question = String(item?.question || '').trim();
    const answer = String(item?.correct_answer || item?.answer || '').trim();
    return question && answer;
  }).length;
};

const countFixedQuestionCsv = (content) => {
  return String(content || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => {
      const [question, answer] = line.split(',').map((cell) => String(cell || '').trim());
      return Boolean(question && answer);
    }).length;
};

export const countFixedQuestionRecords = (content, fileName) => {
  const normalizedName = String(fileName || '').trim().toLowerCase();
  if (normalizedName.endsWith('.json')) return countFixedQuestionJson(content);
  if (normalizedName.endsWith('.csv')) return countFixedQuestionCsv(content);
  return 0;
};

export const getLargestLearningFiles = (files, limit = 5) => {
  return [...(Array.isArray(files) ? files : [])]
    .sort((left, right) => normalizeStorageSize(right?.file_size) - normalizeStorageSize(left?.file_size))
    .slice(0, limit);
};

const getLearningFilePath = (file) => {
  return [file?.file_name, file?.file_url, file?.title]
    .map((value) => String(value || '').trim().toLowerCase())
    .find(Boolean) || '';
};

export const getLearningFilePreviewKind = (file) => {
  const path = getLearningFilePath(file);
  if (path.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path)) return 'image';
  if (/\.(json|csv)$/.test(path)) return 'text';
  return 'unsupported';
};

export const formatLearningPreviewText = (content, file) => {
  const value = String(content || '');
  const path = getLearningFilePath(file);
  if (!path.endsWith('.json')) return value;

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (error) {
    return value;
  }
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

export const validateManualQuestionDraft = (draft = {}) => {
  const options = (draft.options || []).map((option) => String(option || '').trim());
  const errors = [];
  if (!String(draft.question || '').trim()) errors.push('Question text is required.');
  if (options.length !== 4) errors.push('Exactly four answer choices are required.');
  if (options.some((option) => !option)) errors.push('All four answer choices must be nonempty.');
  if (new Set(options.map((option) => option.toLocaleLowerCase())).size !== options.length) errors.push('Answer choices must be distinct.');
  if (options.filter((option) => option.toLocaleLowerCase() === String(draft.correct_answer || '').trim().toLocaleLowerCase()).length !== 1) {
    errors.push('The correct answer must match one of the four choices.');
  }
  return errors;
};

export const getQuestionReviewActionClass = (action) => ({
  add: 'btn btn-success',
  edit: 'btn btn-primary',
  delete: 'btn btn-danger',
  approve: 'btn btn-success',
  close: 'btn btn-secondary',
  download: 'btn btn-secondary',
  push: 'btn btn-primary',
  remove: 'btn btn-warning',
}[String(action || '').trim().toLowerCase()] || 'btn btn-secondary');

export const normalizeLearningFileRecord = (file = {}) => ({
  ...file,
  difficulty: normalizeDifficultyValue(file.difficulty),
  folder_name: getQuestionFolderPath(file.grade_level, file.difficulty),
});

export const filterLearningFiles = (files, filters) => {
  const search = normalizeText(filters.search);
  const folder = normalizeText(filters.folder);
  const gradeLevel = normalizeLearningMetadataValue(filters.grade_level);
  const difficulty = normalizeDifficultyValue(filters.difficulty);
  const mathTopic = normalizeLearningMetadataValue(filters.math_topic);
  const fileType = normalizeLearningMetadataValue(filters.file_type);

  return files.map(normalizeLearningFileRecord).filter((file) => {
    const folderMatch = folder ? normalizeText(file.folder_name) === folder : true;
    const gradeMatch = gradeLevel ? file.grade_level === gradeLevel : true;
    const difficultyMatch = difficulty ? normalizeDifficultyValue(file.difficulty) === difficulty : true;
    const topicMatch = mathTopic ? file.math_topic === mathTopic : true;
    const typeMatch = fileType ? file.file_type === fileType : true;
    const searchMatch = search
      ? [
          file.title,
          file.file_name,
          file.math_topic,
          file.grade_level,
          file.difficulty,
          file.folder_name,
          file.file_type,
        ].join(' ').toLowerCase().includes(search)
      : true;

    return folderMatch && gradeMatch && difficultyMatch && topicMatch && typeMatch && searchMatch;
  });
};

export const getQuestionFolderView = (files, selection = {}, registry) => {
  const gradeLevel = normalizeLearningMetadataValue(selection.grade_level);
  const difficulty = normalizeDifficultyValue(selection.difficulty);
  const questionFolderStructure = getQuestionFolderStructure(registry);
  const path = ['Questions'];
  if (gradeLevel) path.push(gradeLevel);
  if (difficulty) path.push(difficulty);

  if (!gradeLevel) {
    return {
      path,
      childFolders: questionFolderStructure.map((folder) => ({
        type: 'grade',
        label: folder.folderName,
        grade_level: folder.grade,
        godotFolderName: folder.godotFolderName,
      })),
      files: filterLearningFiles(files, {
        search: selection.search || '',
        folder: '',
        grade_level: '',
        difficulty: '',
        math_topic: selection.math_topic || '',
        file_type: selection.file_type || '',
      }),
    };
  }

  if (!difficulty) {
    return {
      path,
      childFolders: getRegistryDimensionOptions(registry, 'difficulties').map((level) => ({
        type: 'difficulty',
        label: level.displayLabel,
        grade_level: gradeLevel,
        difficulty: level.value,
      })),
      files: filterLearningFiles(files, {
        search: selection.search || '',
        folder: '',
        grade_level: gradeLevel,
        difficulty: '',
        math_topic: selection.math_topic || '',
        file_type: selection.file_type || '',
      }),
    };
  }

  return {
    path,
    childFolders: [],
    files: filterLearningFiles(files, {
      search: selection.search || '',
      folder: '',
      grade_level: gradeLevel,
      difficulty,
      math_topic: selection.math_topic || '',
      file_type: selection.file_type || '',
    }),
  };
};

export const getFolderContents = (files, folder) => {
  if (!folder) return [];
  const folderId = folder.id !== undefined && folder.id !== null ? String(folder.id) : '';
  const folderName = normalizeText(folder.name);

  return files.filter((file) => {
    if (folderId) {
      return String(file.folder_id || '') === folderId;
    }
    return folderName ? normalizeText(file.folder_name) === folderName : false;
  });
};
